import { ZButton } from "@/shared/components/button";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { Input, Modal, ModalBody, ModalContent } from "@nextui-org/react";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createQuestionnaire } from "../../requests/questionnaire";
import { useQuestionnaireWizardStore } from "../../states/questionnaire-wizard";
import { startsWithSpecialCharacter, containsProblematicCharacters, containsInvalidNameCharacters } from "@/modules/agent/components/upload-dialog/util";

interface CreateQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateQuestionnaireModal = ({
  isOpen,
  onClose,
}: CreateQuestionnaireModalProps) => {
  const { openWizard } = useQuestionnaireWizardStore();
  const [questionnaireName, setQuestionnaireName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuestionnaireName("");
      setErrorMessage("");
    }
  }, [isOpen]);

  const handleClose = () => {
    setQuestionnaireName("");
    setErrorMessage("");
    onClose();
  };

  const handleProceed = async () => {
    if (!questionnaireName.trim()) return;

    // Validate questionnaire name before API call
    const trimmedName = questionnaireName.trim();
    if (startsWithSpecialCharacter(trimmedName)) {
      setErrorMessage("Input cannot start with a special character");
      return;
    }
    if (containsInvalidNameCharacters(trimmedName)) {
      setErrorMessage("Input can only contain letters, numbers, spaces, hyphens, and underscores.");
      return;
    }
    if (containsProblematicCharacters(trimmedName)) {
      setErrorMessage("Input cannot contain emojis or problematic symbols");
      return;
    }

    setIsCreating(true);
    try {
      // Call create API
      const [error, response] = await parsePromise(createQuestionnaire(trimmedName));

      if (error) {
        // Check for specific error types
        const errorType = error?.response?.data?.error_type;
        const errorMessage = error?.response?.data?.error_message;

        if (errorMessage.includes("already exists")) {
          setErrorMessage("Name already exists. Please choose a different name to proceed.");
          return;
        }

        // Handle error_type that might be a string or array
        const isPermissionDeniedError = Array.isArray(errorType)
          ? errorType.includes("Permission Denied Error")
          : errorType === "Permission Denied Error";

       
        // Extract and display the actual API error message for other errors
        let apiErrorMessage = error?.message || "An unexpected error occurred. Please try again later.";

        // Handle error_message that might be a string or array
        if (!apiErrorMessage || apiErrorMessage === "An unexpected error occurred. Please try again later.") {
          const errorData = error?.response?.data?.error_message;
          if (Array.isArray(errorData)) {
            apiErrorMessage = errorData[0] || "An unexpected error occurred. Please try again later.";
          } else if (typeof errorData === 'string') {
            apiErrorMessage = errorData;
          }
        }

        setErrorMessage(apiErrorMessage);
        return;
      }

      if (response?.success && response.questionnaire) {
        // Show success notification
        addNotification({
          type: "success",
          message: `"${response.questionnaire.questionnaire_name}" created successfully.`,
        });

        // Close modal first
        handleClose();

        // Open wizard in edit mode with the newly created questionnaire ID
        setTimeout(() => {
          openWizard(response.questionnaire.id);
        }, 100);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Create questionnaire error:", error);
      // For unexpected errors (not API validation errors), show generic message
      setErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setIsCreating(false);
    }
  };

  const isCreateDisabled = !questionnaireName.trim() || isCreating || !!errorMessage;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      hideCloseButton={true}
      classNames={{
        base: "max-w-md",
        wrapper: "z-[9999] p-2",
      }}
    >
      <ModalContent className="max-h-[500px]">
        <ModalBody className="p-6">
          <div className="flex flex-col gap-6 w-full">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-zinc-900 text-center flex-grow">
                Create Questionnaire & Document Set              </h3>
              <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="w-full flex flex-col items-start gap-1.5">
              <label htmlFor="questionnaireName" className="text-sm font-normal text-[#18181B]">
                Name of Questionnaire & Document Set
              </label>
              <Input
                id="questionnaireName"
                placeholder="Enter name of new questionnaire"
                value={questionnaireName}
                onChange={(e) => {
                  const newValue = e.target.value.trimStart();
                  setQuestionnaireName(newValue);
                  
                  // Clear error message first
                  setErrorMessage("");
                  
                  // Validate input if not empty
                  if (newValue) {
                    if (startsWithSpecialCharacter(newValue)) {
                      setErrorMessage("Input cannot start with a special character");
                    } else if (containsInvalidNameCharacters(newValue)) {
                      setErrorMessage("Name cannot contain special characters, except hyphens and underscores.");
                    } 
                  }
                }}
                maxLength={50}
                className="w-full"
                classNames={{
                  inputWrapper: `bg-[#FAFAFA] border rounded-lg h-auto px-4 py-2 ${errorMessage ? 'border-[#F31260]' : 'border-[#E4E4E7]'}`,
                  input: "text-sm placeholder:text-[#A1A1AA]",
                }}
                variant="flat"
                isInvalid={!!errorMessage}
              />
              {errorMessage && (
                <div className="text-[#F31260] text-xs mt-1 px-1">
                  {errorMessage}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 pt-1 mt-auto">
              <div className="w-full border-t border-[#DCDCDC]" />
              <div className="flex justify-end gap-2 w-full pt-2">
                <ZButton
                  variant="bordered"
                  onClick={handleClose}
                  className="h-7 px-3 text-sm rounded-md border-[#006FEE] text-[#006FEE] font-medium"
                >
                  Cancel
                </ZButton>
                <ZButton
                  color="primary"
                  onClick={handleProceed}
                  isDisabled={isCreateDisabled}
                  isLoading={isCreating}
                  className="h-7 px-3 text-sm rounded-md bg-[#006FEE] text-white font-medium"
                >
                  Proceed
                </ZButton>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateQuestionnaireModal;
